import { SITE } from "./site";

// These labels also name the visible service cards. Keep their wording shared.
export const SERVICE_KEYWORDS = [
  "Taxi Services",
  "Car Hire",
  "Car Rental",
  "Cab Booking",
  "Tempo Traveller on Rental",
  "Tempo Traveller on Hire",
  "Tempo Traveller on Booking",
];

export const SERVICE_KEYS = Object.freeze([
  "taxi-services", "car-hire", "car-rental", "cab-booking",
  "tempo-traveller-on-rental", "tempo-traveller-on-hire", "tempo-traveller-on-booking",
]);

export const STATIC_SERVICE_SEO = {
  home: {
    title: "Satna Taxi Services, Car Hire & Car Rental | Cab Booking",
    description: "Arrange taxi services, car hire, car rental and cab booking from Satna for local trips, Maihar visits and outstation travel. Enquire for Tempo Traveller hire.",
    h1: "Taxi Services, Car Hire & Car Rental in Satna",
  },
  services: {
    title: "Taxi Services & Car Hire in Satna | Cab Booking Options",
    description: "Explore taxi services, car hire and cab booking for Satna city travel, station transfers and temple trips. Plan Tempo Traveller rental for your group itinerary.",
    h1: "Taxi Services & Car Hire for Local and Outstation Travel",
  },
  tempo: {
    title: "Tempo Traveller Rental & Hire in Satna | 13–26 Seater Rates",
    description: "Compare Tempo Traveller rental, hire and booking in Satna for 13–26 total seats. View local and outstation rates; 9-seater quotes, car hire and cab booking available.",
    h1: "Tempo Traveller Rental, Hire & Booking in Satna",
  },
  fleet: {
    title: "Car Hire & Tempo Traveller Rates in Satna | Taxi Fleet",
    description: "Compare cars, SUVs and Tempo Travellers for Satna taxi services, car hire and cab booking. Check rental rates, passenger seats, fuel terms and applicable extras.",
    h1: "Car Hire & Tempo Traveller Rates for Your Journey",
  },
  areas: {
    title: "Taxi Services & Car Hire Areas | 26 Destinations from Satna",
    description: "Browse 26 destinations for taxi services, car hire and cab booking from our Satna base. Find pickup guidance, connected routes and Tempo Traveller booking options.",
    h1: "Taxi Services & Car Hire Across Our 26 Service Areas",
  },
};

const SERVICE_DESCRIPTIONS = [
  "Local and outstation taxi journeys with pickup arrangements and the full itinerary confirmed before travel.",
  "Car hire with a driver for family, business and sightseeing journeys, subject to vehicle availability.",
  "Car rental with a driver for local packages, outstation journeys and multi-day trips; the vehicle, fare and itinerary are confirmed before booking.",
  "Cab booking enquiries for pickup, drop and return journeys, with the fare and schedule confirmed directly.",
  "Tempo Traveller rental for group trips, with passenger seats, luggage and the journey plan agreed before booking.",
  "Tempo Traveller hire for pilgrimage groups, wedding guests and multi-day travel, subject to availability.",
  "Tempo Traveller booking enquiries by phone, WhatsApp or the form; an enquiry does not confirm a vehicle.",
];

const canonical = route => {
  const pathname = String(route || "/").replace(/^\/+|\/+$/g, "");
  return `${SITE.domain.replace(/\/+$/, "")}${pathname ? `/${pathname}` : ""}/`;
};

// Returns a Service schema. Callers may override its type or areaServed while
// retaining the seven-item catalog. `location` accepts the existing city record.
// Offers deliberately contain no invented price or instant-booking promise.
export function buildServiceCatalogSchema({ city = SITE.baseCity, route = "/", description = "", location } = {}) {
  const serviceCity = location?.city || city;
  const url = canonical(route);
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": url + "#travel-service",
    name: `Taxi, car hire, car rental and Tempo Traveller services in ${serviceCity}`,
    description: description || `Taxi services, car hire, car rental, cab booking and Tempo Traveller travel for ${serviceCity}. Confirm the route, vehicle availability and final quotation before booking.`,
    serviceType: [...SERVICE_KEYWORDS],
    url,
    provider: {
      "@type": "LocalBusiness",
      "@id": `${canonical("/")}#localbusiness`,
      name: SITE.brand,
      url: canonical("/"),
      telephone: SITE.phonePrimary.tel,
      email: SITE.email,
      address: {
        "@type": "PostalAddress",
        addressLocality: SITE.baseCity,
        addressRegion: SITE.region,
        addressCountry: "IN",
      },
    },
    areaServed: { "@type": "Place", name: serviceCity },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      "@id": url + "#service-catalog",
      name: `Taxi and Tempo Traveller services for ${serviceCity}`,
      itemListElement: SERVICE_KEYWORDS.map((name, index) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          "@id": url + "#offered-service-" + SERVICE_KEYS[index],
          name,
          serviceType: name,
          description: SERVICE_DESCRIPTIONS[index],
          url: url + "#service-" + SERVICE_KEYS[index],
        },
      })),
    },
  };
}

// Keep the authored local intent; rental synonyms belong in helpful page copy,
// rather than replacing distinctive routes or Tempo Traveller title wording.
export function buildLocationServiceSeo(location) {
  return { title: location.title, description: location.description };
}
